package com.evoapps.evoshift.widgets

import com.evoapps.evoshift.ui.theme.animateItemIfMotion
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.OpenInNew
import androidx.compose.ui.platform.testTag
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.Check
import androidx.compose.material.icons.filled.Settings
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.ModalBottomSheet
import androidx.compose.material3.Text
import androidx.compose.material3.rememberModalBottomSheetState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.Immutable
import androidx.compose.runtime.staticCompositionLocalOf
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.evoapps.evoshift.domain.model.Roster
import com.evoapps.evoshift.domain.model.RosterStatus
import com.evoapps.evoshift.ui.DarkGold
import com.evoapps.evoshift.ui.DarkMetallicBackgroundBrush
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.GoldMetallicBrush
import com.evoapps.evoshift.ui.LightGold
import com.evoapps.evoshift.ui.adaptive.handCursor
import com.evoapps.evoshift.ui.util.label
import com.evoapps.evoshift.ui.util.monthName
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.context_add_workplace
import evoshift.composeapp.generated.resources.context_edit_workplace
import evoshift.composeapp.generated.resources.context_no_rosters
import evoshift.composeapp.generated.resources.context_rosters
import evoshift.composeapp.generated.resources.context_your_workplaces
import evoshift.composeapp.generated.resources.workplace
import evoshift.composeapp.generated.resources.open_in_new_window
import org.jetbrains.compose.resources.stringResource

/**
 * Opens the app-wide workplace chooser (hosted once, in App.kt): the top bar's workplace and
 * More's "Switch workplace" open the same sheet. The month is the top bar's (SH-89).
 */
/** Test tags of the workplace chooser. */
object ContextSwitchTags {
    fun newWindow(workplaceName: String) = "context_new_window_$workplaceName"
}

@Immutable
class ContextSwitcher(val open: () -> Unit)

val LocalContextSwitcher = staticCompositionLocalOf { ContextSwitcher {} }

/**
 * The workplace chooser: picking one switches at once and closes the sheet; "Add workplace" and
 * the current workplace's settings are here too. The months are the top bar's (SH-89).
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun ContextSwitchBottomSheet(
    onDismiss: () -> Unit,
    workplaces: Map<String, String>,
    currentWorkplaceId: String,
    onWorkplaceSelected: (String) -> Unit,
    onAddWorkplace: (() -> Unit)?,
    onEditWorkplace: ((String) -> Unit)?,
    /** SH-161 F3: a workplace in a window of its own (large windows and pointer devices); null: not offered. */
    onOpenInNewWindow: ((String) -> Unit)? = null,
) {
    ModalBottomSheet(
        onDismissRequest = onDismiss,
        sheetState = rememberModalBottomSheetState(skipPartiallyExpanded = true),
        containerColor = DeepNavy,
        shape = RoundedCornerShape(topStart = 28.dp, topEnd = 28.dp),
        dragHandle = {
            Box(
                modifier = Modifier
                    .padding(top = 12.dp, bottom = 4.dp)
                    .width(36.dp)
                    .height(4.dp)
                    .background(GoldMetallicBrush, RoundedCornerShape(2.dp))
            )
        }
    ) {
        ProvideDialogColors {
            LazyColumn(
                modifier = Modifier
                    .fillMaxWidth()
                    .background(DarkMetallicBackgroundBrush)
                    .navigationBarsPadding()
                    // SH-98: Esc closes the chooser.
                    .dialogKeys(onEscape = onDismiss, onEnter = null),
                contentPadding = PaddingValues(horizontal = 20.dp, vertical = 12.dp),
                verticalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                item(key = "wp-header") { SectionTitle(stringResource(Res.string.context_your_workplaces)) }
                items(workplaces.toList(), key = { "wp-${it.first}" }) { (id, name) ->
                    Box(Modifier.animateItemIfMotion(this), propagateMinConstraints = true) {
                        WorkplaceRow(
                            name = name,
                            isSelected = id == currentWorkplaceId,
                            onClick = { onWorkplaceSelected(id) },
                            // Settings of the current workplace only: the role there is the one we know.
                            onEditClick = onEditWorkplace?.takeIf { id == currentWorkplaceId }?.let { edit -> { edit(id) } },
                            onOpenInNewWindow = onOpenInNewWindow?.let { open -> { open(id) } }
                        )
                    }
                }
                if (onAddWorkplace != null) {
                    item(key = "wp-add") {
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .clip(RoundedCornerShape(14.dp))
                                .handCursor()
                                .clickable(onClick = onAddWorkplace)
                                .padding(horizontal = 14.dp, vertical = 12.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Icon(Icons.Default.Add, null, tint = DarkGold, modifier = Modifier.size(20.dp))
                            Spacer(Modifier.width(12.dp))
                            Text(stringResource(Res.string.context_add_workplace), color = LightGold, fontWeight = FontWeight.SemiBold)
                        }
                    }
                }

                item(key = "bottom") { Spacer(Modifier.height(16.dp)) }
            }
        }
    }
}

@Composable
private fun SectionTitle(text: String) {
    Text(
        text.uppercase(),
        style = TextStyle(brush = GoldMetallicBrush, fontSize = 12.sp, fontWeight = FontWeight.Bold, letterSpacing = 1.2.sp),
        modifier = Modifier.padding(top = 4.dp, bottom = 4.dp)
    )
}

@Composable
fun WorkplaceRow(
    name: String,
    isSelected: Boolean,
    onClick: () -> Unit,
    onEditClick: (() -> Unit)?,
    onOpenInNewWindow: (() -> Unit)? = null
) {
    val shape = RoundedCornerShape(14.dp)
    val newWindowLabel = stringResource(Res.string.open_in_new_window)
    // SH-161 F3: the right-click menu offers the new window too (SH-98 menus).
    ContextMenuArea(
        items = { listOfNotNull(onOpenInNewWindow?.let { ContextMenuItem(newWindowLabel, it, Icons.AutoMirrored.Filled.OpenInNew) }) },
        enabled = onOpenInNewWindow != null
    ) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .heightIn(min = 56.dp)
            .clip(shape)
            .background(if (isSelected) Color.White.copy(alpha = 0.08f) else Color.Transparent)
            .border(
                if (isSelected) BorderStroke(1.dp, GoldMetallicBrush) else BorderStroke(1.dp, Color.White.copy(alpha = 0.14f)),
                shape
            )
            .handCursor()
            .clickable(onClick = onClick)
            .padding(start = 14.dp, end = 4.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Initial in a gold ring: workplaces are recognisable at a glance.
        Box(
            modifier = Modifier
                .size(32.dp)
                .clip(CircleShape)
                .background(if (isSelected) GoldMetallicBrush else SolidColor(Color.White.copy(alpha = 0.1f))),
            contentAlignment = Alignment.Center
        ) {
            Text(
                name.trim().firstOrNull()?.uppercase() ?: "•",
                color = if (isSelected) DeepNavy else Color.White,
                fontWeight = FontWeight.Bold
            )
        }
        Spacer(Modifier.width(12.dp))
        Text(
            text = name.ifBlank { stringResource(Res.string.workplace) },
            style = MaterialTheme.typography.titleMedium,
            color = Color.White,
            fontWeight = if (isSelected) FontWeight.Bold else FontWeight.Medium,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis,
            modifier = Modifier.weight(1f)
        )
        if (isSelected) Icon(Icons.Default.Check, null, tint = DarkGold, modifier = Modifier.size(20.dp))
        if (onOpenInNewWindow != null) {
            IconButtonWithTooltip(onClick = onOpenInNewWindow, modifier = Modifier.handCursor().testTag(ContextSwitchTags.newWindow(name)), tooltip = newWindowLabel) {
                Icon(
                    Icons.AutoMirrored.Filled.OpenInNew,
                    contentDescription = newWindowLabel,
                    tint = Color.White.copy(alpha = 0.6f),
                    modifier = Modifier.size(20.dp)
                )
            }
        }
        if (onEditClick != null) {
            IconButtonWithTooltip(onClick = onEditClick, modifier = Modifier.handCursor(), tooltip = stringResource(Res.string.context_edit_workplace)) {
                Icon(
                    Icons.Default.Settings,
                    contentDescription = stringResource(Res.string.context_edit_workplace),
                    tint = Color.White.copy(alpha = 0.6f),
                    modifier = Modifier.size(20.dp)
                )
            }
        } else {
            Spacer(Modifier.width(12.dp))
        }
    }
    }
}
